/**
 * ThirdPartyLabels — BabyQuip Google Calendar label generator.
 *
 * Shows a monthly calendar with delivery (green) and pickup (amber) dots.
 * Click any day → order panel slides in on the right.
 *
 * Selection model (label keys = "{code}::{itemIdx}::{unit}"):
 *   - Order header checkbox → select/deselect ALL labels in that order
 *   - Item row checkbox     → select/deselect ALL units of one item type
 *   - Unit sub-row checkbox → select/deselect ONE specific label (qty > 1)
 * "Print Selected" prints only checked labels. Printed cards turn green.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Link2,
  Link2Off,
  Printer,
  Package,
  MapPin,
  Clock,
  RefreshCw,
  X,
  CheckSquare2,
  Square,
  MinusSquare,
  CheckCircle2,
} from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

// ── date helpers ──────────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().slice(0, 10);

const monthStart = ({ year, month }) =>
  `${year}-${String(month).padStart(2, "0")}-01`;

const monthEnd = ({ year, month }) => {
  const last = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
};

const monthLabel = ({ year, month }) =>
  new Date(year, month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

const addMonth = ({ year, month }, delta) => {
  let m = month + delta;
  let y = year;
  if (m > 12) { m -= 12; y += 1; }
  if (m < 1)  { m += 12; y -= 1; }
  return { year: y, month: m };
};

const buildGrid = ({ year, month }) => {
  const first = new Date(year, month - 1, 1).getDay();
  const days  = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) {
    cells.push(`${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

// ── label key helpers ─────────────────────────────────────────────────────────
// A label key uniquely identifies one physical label:
//   "{confirmation_code}::{itemIndex}::{unitNumber}"

const makeLabelKey = (code, itemIdx, unit) => `${code}::${itemIdx}::${unit}`;

/** Return all label keys for a single item */
const itemLabelKeys = (code, itemIdx, qty) =>
  Array.from({ length: qty }, (_, n) => makeLabelKey(code, itemIdx, n + 1));

/** Return all label keys for an entire order */
const orderLabelKeys = (order) =>
  order.items.flatMap((item, ii) => itemLabelKeys(order.confirmation_code, ii, item.qty));

// ── localStorage helpers for "printed" tracking ───────────────────────────────
const PRINTED_KEY = "enchanted_printed_codes";

const loadPrinted = () => {
  try { return new Set(JSON.parse(localStorage.getItem(PRINTED_KEY) || "[]")); }
  catch { return new Set(); }
};
const savePrinted = (set) => {
  try { localStorage.setItem(PRINTED_KEY, JSON.stringify([...set])); } catch { /* noop */ }
};

// ── connect banner ────────────────────────────────────────────────────────────
const ConnectBanner = ({ onConnect, connError, needsReconnect = false }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-5 text-center">
    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border ${needsReconnect ? "bg-amber-500/10 border-amber-400/25" : "bg-[#FFD166]/10 border-[#FFD166]/25"}`}>
      {needsReconnect ? (
        <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
      ) : (
        <svg className="w-7 h-7 text-[#FFD166]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )}
    </div>
    <div>
      <h2 className="text-xl font-semibold text-[#F6F1E4]">
        {needsReconnect ? "Reconnect Google Calendar" : "Connect Google Calendar"}
      </h2>
      <p className="text-sm text-[#B9C8D5] mt-1 max-w-sm mx-auto">
        {needsReconnect
          ? "The Calendar permission is missing or expired. Disconnect and reconnect, then on Google's consent screen check the box to allow access to your Calendar events."
          : "Authorize once to see your BabyQuip orders on a calendar and print labels for any day or order."}
      </p>
      {needsReconnect && (
        <p className="text-xs text-amber-400/80 mt-2 max-w-sm mx-auto">
          On the Google consent page, make sure to check "See your primary Google Calendar" when prompted.
        </p>
      )}
    </div>
    <Btn
      onClick={onConnect}
      data-testid="gcal-connect-btn"
      className={`gap-2 ${needsReconnect ? "bg-amber-400 text-[#0B1B2B] hover:bg-amber-300" : ""}`}
    >
      <Link2 className="w-4 h-4" />
      {needsReconnect ? "Reconnect with Google" : "Connect with Google"}
    </Btn>
    {connError && <p className="text-xs text-red-400">{connError}</p>}
  </div>
);

// ── tiny checkbox icon helper ─────────────────────────────────────────────────
const CheckIcon = ({ state, size = "w-4 h-4" }) => {
  if (state === "all")     return <CheckSquare2 className={`${size} text-[#FFD166]`} />;
  if (state === "partial") return <MinusSquare  className={`${size} text-[#FFD166]/60`} />;
  return <Square className={`${size} text-[#B9C8D5]`} />;
};

const selectionState = (keys, set) => {
  if (!keys.length) return "none";
  const hits = keys.filter(k => set.has(k)).length;
  if (hits === 0) return "none";
  if (hits === keys.length) return "all";
  return "partial";
};

// ── order card ────────────────────────────────────────────────────────────────
const OrderCard = ({
  order,
  onPrintOrder,
  printing,
  showPrint = true,
  selectedLabelKeys,
  onToggleOrder,
  onToggleItem,
  onToggleKey,
  printed,
}) => {
  const [open, setOpen] = useState(true);

  const allKeys   = orderLabelKeys(order);
  const orderSel  = selectionState(allKeys, selectedLabelKeys || new Set());

  const borderColor = printed
    ? "border-green-500/25 bg-green-500/5"
    : orderSel !== "none"
      ? "border-[#FFD166]/35 bg-[#FFD166]/5"
      : "border-white/10 bg-white/3";

  return (
    <div className={`rounded-xl border overflow-hidden transition-colors ${borderColor}`}>
      {/* ── header row ── */}
      <div className="flex items-center gap-1.5 px-2 py-2.5">
        {showPrint && (
          <button
            type="button"
            onClick={() => onToggleOrder?.(order)}
            className="shrink-0 hover:opacity-80 transition-opacity"
            data-testid={`order-check-${order.confirmation_code}`}
            title={orderSel === "all" ? "Deselect order" : "Select all labels in order"}
          >
            <CheckIcon state={orderSel} />
          </button>
        )}

        <button
          type="button"
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
          onClick={() => setOpen(o => !o)}
          data-testid={`order-header-${order.confirmation_code}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-mono text-[#FFD166] text-[10px]">#{order.confirmation_code}</span>
              <span className={`text-sm font-semibold truncate ${printed ? "text-[#B9C8D5]" : "text-[#F6F1E4]"}`}>
                {order.customer_name}
              </span>
              {printed && (
                <span className="flex items-center gap-0.5 text-[9px] text-green-400 font-semibold bg-green-500/15 px-1.5 py-0.5 rounded-full shrink-0">
                  <CheckCircle2 className="w-2.5 h-2.5" /> Printed
                </span>
              )}
            </div>
            <div className="text-[10px] text-[#B9C8D5] mt-0.5 flex gap-2">
              <span className="text-green-400">D: {order.delivery_date}</span>
              <span className="text-amber-400">P: {order.pickup_date}</span>
            </div>
            {order.parse_warning && (
              <div className="text-[10px] text-orange-400 mt-0.5">{order.parse_warning}</div>
            )}
          </div>
        </button>

        {showPrint && (
          <Btn
            onClick={e => { e.stopPropagation(); onPrintOrder(order); }}
            disabled={printing}
            className="shrink-0 !py-1 !px-2 text-xs"
            data-testid={`print-order-${order.confirmation_code}`}
            title="Print all labels for this order"
          >
            <Printer className="w-3 h-3" /> {allKeys.length}
          </Btn>
        )}
      </div>

      {/* ── expanded body ── */}
      {open && (
        <div className="border-t border-white/8 px-3 py-2 space-y-1">
          {order.delivery_address && (
            <p className="flex items-start gap-1 text-[10px] text-[#B9C8D5] mb-1.5">
              <MapPin className="w-3 h-3 shrink-0 mt-0.5 text-[#FFD166]" />
              {order.delivery_address}
            </p>
          )}
          <div className="flex gap-3 text-[10px] mb-2">
            <span className="flex items-center gap-1 text-green-400">
              <Clock className="w-3 h-3" /> {order.delivery_window}
            </span>
            <span className="flex items-center gap-1 text-amber-400">
              <Clock className="w-3 h-3" /> {order.pickup_window}
            </span>
          </div>

          {/* ── items with per-item and per-unit checkboxes ── */}
          <div className="space-y-1.5">
            {order.items.map((item, ii) => {
              const iKeys   = itemLabelKeys(order.confirmation_code, ii, item.qty);
              const itemSel = selectionState(iKeys, selectedLabelKeys || new Set());

              return (
                <div key={ii}>
                  {/* item row */}
                  <div className="flex items-center gap-2 text-[11px]">
                    {showPrint && (
                      <button
                        type="button"
                        onClick={() => onToggleItem?.(iKeys)}
                        className="shrink-0 hover:opacity-80 transition-opacity"
                        data-testid={`item-check-${order.confirmation_code}-${ii}`}
                        title={itemSel === "all" ? "Deselect this item" : "Select all labels for this item"}
                      >
                        <CheckIcon state={itemSel} size="w-3.5 h-3.5" />
                      </button>
                    )}
                    <Package className="w-3 h-3 text-[#B9C8D5] shrink-0" />
                    <span className="text-[#F6F1E4] flex-1 truncate">{item.name}</span>
                    <span className="font-mono text-[#FFD166]">×{item.qty}</span>
                  </div>

                  {/* unit sub-rows — only shown when qty > 1 */}
                  {showPrint && item.qty > 1 && (
                    <div className="ml-5 mt-1 space-y-0.5">
                      {iKeys.map((key, n) => {
                        const unitSel = selectedLabelKeys?.has(key) ? "all" : "none";
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => onToggleKey?.(key)}
                            className="flex items-center gap-1.5 w-full text-[10px] text-[#B9C8D5] hover:text-[#F6F1E4] transition-colors"
                            data-testid={`unit-check-${order.confirmation_code}-${ii}-${n + 1}`}
                            title={`Label ${n + 1} of ${item.qty}`}
                          >
                            <CheckIcon state={unitSel} size="w-3 h-3" />
                            <span>Label {n + 1} of {item.qty}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── main component ────────────────────────────────────────────────────────────
export const ThirdPartyLabels = () => {
  const now = new Date();
  const [calMonth, setCalMonth] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [calStatus, setCalStatus]     = useState(null);
  const [connError, setConnError]     = useState("");
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [monthOrders, setMonthOrders] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [printing, setPrinting]       = useState(false);
  const [labelSize, setLabelSize]     = useState("large");
  // label-key level selection & printed tracking
  const [selectedLabelKeys, setSelectedLabelKeys] = useState(new Set());
  const [printedCodes, setPrintedCodes] = useState(loadPrinted);
  const panelRef = useRef(null);

  // Clear selection when day changes
  useEffect(() => { setSelectedLabelKeys(new Set()); }, [selectedDay]);

  // ── auth status ──────────────────────────────────────────────────────────────
  const checkStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/google-calendar/status`);
      setCalStatus(data);
      // If status already signals a broken token, surface reconnect immediately
      if (data.connected && data.needs_reconnect) {
        setNeedsReconnect(true);
        setCalStatus(prev => ({ ...prev, connected: false }));
      }
    } catch {
      setCalStatus({ connected: false, email: "" });
    }
  }, []);

  useEffect(() => {
    checkStatus();
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("gcal_connected") === "1") {
      toast.success("Google Calendar connected!");
      const url = new URL(window.location.href);
      url.searchParams.delete("gcal_connected");
      window.history.replaceState({}, "", url.toString());
    } else if (sp.get("gcal_error")) {
      setConnError(`Connection failed: ${sp.get("gcal_error")}`);
      const url = new URL(window.location.href);
      url.searchParams.delete("gcal_error");
      window.history.replaceState({}, "", url.toString());
    }
  }, [checkStatus]);

  // ── load month ───────────────────────────────────────────────────────────────
  const handleGoogleError = (e) => {
    const status = e.response?.status;
    const detail = e.response?.data?.detail;
    // FastAPI wraps detail dict as { "detail": { needs_reconnect: true, ... } }
    if (status === 409 && detail?.needs_reconnect) {
      setNeedsReconnect(true);
      setCalStatus(prev => ({ ...prev, connected: false }));
      return true; // handled — caller should NOT toast
    }
    return false;
  };

  /** Safe string to pass to toast from an axios error */
  const errMsg = (e, fallback) => {
    const d = e.response?.data?.detail;
    return typeof d === "string" ? d : (d?.message || fallback);
  };

  const loadMonth = useCallback(async (mon) => {
    if (!calStatus?.connected) return;
    setLoadingMonth(true);
    try {
      const { data } = await axios.get(`${API}/admin/google-calendar/events-range`, {
        params: { start_date: monthStart(mon), end_date: monthEnd(mon) },
      });
      setMonthOrders(data.orders || []);
      setNeedsReconnect(false);
    } catch (e) {
      if (!handleGoogleError(e))
        toast.error(errMsg(e, "Failed to load calendar events."));
    } finally {
      setLoadingMonth(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calStatus]);

  useEffect(() => {
    if (calStatus?.connected) loadMonth(calMonth);
  }, [calStatus, calMonth, loadMonth]);

  const handleRefresh = async () => {
    if (loadingMonth) return;
    setLoadingMonth(true);
    try {
      const { data } = await axios.get(`${API}/admin/google-calendar/events-range`, {
        params: { start_date: monthStart(calMonth), end_date: monthEnd(calMonth) },
      });
      setMonthOrders(data.orders || []);
      setNeedsReconnect(false);
      toast.success("Calendar refreshed");
    } catch (e) {
      if (!handleGoogleError(e))
        toast.error(errMsg(e, "Refresh failed."));
    } finally {
      setLoadingMonth(false);
    }
  };

  // ── day index ────────────────────────────────────────────────────────────────
  const dayMap = {};
  monthOrders.forEach(order => {
    const toISO = (str) => {
      if (!str) return null;
      try { const d = new Date(str); return isNaN(d) ? null : d.toISOString().slice(0, 10); }
      catch { return null; }
    };
    const dd = toISO(order.delivery_date);
    const pd = toISO(order.pickup_date);
    if (dd) {
      if (!dayMap[dd]) dayMap[dd] = { deliveries: [], pickups: [] };
      dayMap[dd].deliveries.push(order);
    }
    if (pd) {
      if (!dayMap[pd]) dayMap[pd] = { deliveries: [], pickups: [] };
      dayMap[pd].pickups.push(order);
    }
  });

  const dayOrders = selectedDay
    ? [...(dayMap[selectedDay]?.deliveries || []), ...(dayMap[selectedDay]?.pickups || [])]
        .filter((o, i, arr) => arr.findIndex(x => x.confirmation_code === o.confirmation_code) === i)
    : [];

  const deliveryOrders = selectedDay ? (dayMap[selectedDay]?.deliveries || []) : [];

  // All possible label keys for the current day's deliveries
  const allDayDeliveryKeys = deliveryOrders.flatMap(orderLabelKeys);

  // ── printed tracking ─────────────────────────────────────────────────────────
  const markPrinted = (codes) => {
    const next = new Set([...printedCodes, ...codes]);
    setPrintedCodes(next);
    savePrinted(next);
  };

  // ── selection helpers ────────────────────────────────────────────────────────
  const toggleKey = (key) => {
    setSelectedLabelKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleItemKeys = (keys) => {
    setSelectedLabelKeys(prev => {
      const next = new Set(prev);
      const allSel = keys.every(k => next.has(k));
      allSel ? keys.forEach(k => next.delete(k)) : keys.forEach(k => next.add(k));
      return next;
    });
  };

  const toggleOrderKeys = (order) => {
    const keys = orderLabelKeys(order);
    setSelectedLabelKeys(prev => {
      const next = new Set(prev);
      const allSel = keys.every(k => next.has(k));
      allSel ? keys.forEach(k => next.delete(k)) : keys.forEach(k => next.add(k));
      return next;
    });
  };

  const allDaySel = selectionState(allDayDeliveryKeys, selectedLabelKeys);

  const toggleSelectAll = () => {
    setSelectedLabelKeys(prev => {
      const next = new Set(prev);
      if (allDayDeliveryKeys.every(k => next.has(k))) {
        allDayDeliveryKeys.forEach(k => next.delete(k));
      } else {
        allDayDeliveryKeys.forEach(k => next.add(k));
      }
      return next;
    });
  };

  // Count of delivery label keys that are currently selected
  const selectedDayKeyCount = allDayDeliveryKeys.filter(k => selectedLabelKeys.has(k)).length;

  // ── print helpers ────────────────────────────────────────────────────────────
  const printLabels = async (labels, codesToMark = []) => {
    if (!labels.length) { toast.warning("No labels to print."); return; }
    setPrinting(true);
    try {
      const resp = await axios.post(
        `${API}/admin/babyquip-labels-pdf`,
        { labels, size: labelSize },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      if (codesToMark.length) {
        markPrinted(codesToMark);
        setSelectedLabelKeys(new Set());
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF generation failed.");
    } finally {
      setPrinting(false);
    }
  };

  const buildLabels = (orders) => {
    const labels = [];
    orders.forEach(order => {
      order.items.forEach(item => {
        for (let n = 1; n <= item.qty; n++) {
          labels.push({
            product_name:      item.name,
            customer_name:     order.customer_name,
            delivery_date:     order.delivery_date,
            delivery_window:   order.delivery_window,
            pickup_date:       order.pickup_date,
            pickup_window:     order.pickup_window,
            delivery_address:  order.delivery_address,
            delivery_details:  order.delivery_details || "",
            location_type:     order.location_type || "",
            confirmation_code: order.confirmation_code,
            unit_number:       n,
            unit_total:        item.qty,
          });
        }
      });
    });
    return labels;
  };

  /** Build labels only for the currently selected keys */
  const buildSelectedLabels = () => {
    const labels = [];
    deliveryOrders.forEach(order => {
      order.items.forEach((item, ii) => {
        for (let n = 1; n <= item.qty; n++) {
          const key = makeLabelKey(order.confirmation_code, ii, n);
          if (selectedLabelKeys.has(key)) {
            labels.push({
              product_name:      item.name,
              customer_name:     order.customer_name,
              delivery_date:     order.delivery_date,
              delivery_window:   order.delivery_window,
              pickup_date:       order.pickup_date,
              pickup_window:     order.pickup_window,
              delivery_address:  order.delivery_address,
              delivery_details:  order.delivery_details || "",
              location_type:     order.location_type || "",
              confirmation_code: order.confirmation_code,
              unit_number:       n,
              unit_total:        item.qty,
            });
          }
        }
      });
    });
    return labels;
  };

  const handlePrintDay = () =>
    printLabels(buildLabels(deliveryOrders), deliveryOrders.map(o => o.confirmation_code));

  const handlePrintSelected = () => {
    const labels = buildSelectedLabels();
    const codes  = [...new Set(labels.map(l => l.confirmation_code))];
    printLabels(labels, codes);
  };

  const handlePrintOrder = (order) =>
    printLabels(buildLabels([order]), [order.confirmation_code]);

  // ── connect / disconnect ─────────────────────────────────────────────────────
  const handleConnect = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/google-calendar/auth-url`);
      window.location.href = data.auth_url;
    } catch { toast.error("Could not start Google authorization."); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try {
      await axios.delete(`${API}/admin/google-calendar/disconnect`);
      setCalStatus({ connected: false, email: "" });
      setMonthOrders([]);
      setNeedsReconnect(false);
      toast.success("Disconnected.");
    } catch { toast.error("Disconnect failed."); }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  if (calStatus === null) {
    return <div className="text-center text-[#B9C8D5] py-16 animate-pulse">Loading…</div>;
  }
  if (!calStatus.connected) {
    return <ConnectBanner onConnect={handleConnect} connError={connError} needsReconnect={needsReconnect} />;
  }

  const grid  = buildGrid(calMonth);
  const DOW   = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = todayStr();
  const totalDayLabels = deliveryOrders.reduce((s, o) => s + o.items.reduce((a, i) => a + i.qty, 0), 0);

  return (
    <div className="space-y-4" data-testid="third-party-labels">
      {/* connected bar + size toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-0.5">
        <div className="flex items-center gap-2 text-xs text-[#B9C8D5]">
          <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
          Connected: <span className="text-[#F6F1E4] font-medium">{calStatus.email}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-lg border border-white/10 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setLabelSize("large")}
              className={`px-2.5 py-1 rounded-md transition-colors ${labelSize === "large" ? "bg-[#FFD166] text-[#0B1B2B] font-semibold" : "text-[#B9C8D5] hover:text-[#F6F1E4]"}`}
              data-testid="label-size-large"
            >
              Large
            </button>
            <button
              type="button"
              onClick={() => setLabelSize("compact")}
              className={`px-2.5 py-1 rounded-md transition-colors ${labelSize === "compact" ? "bg-[#FFD166] text-[#0B1B2B] font-semibold" : "text-[#B9C8D5] hover:text-[#F6F1E4]"}`}
              data-testid="label-size-compact"
            >
              2.4″ × 1.75″
            </button>
          </div>
          <button
            type="button" onClick={handleDisconnect}
            className="flex items-center gap-1 text-xs text-[#B9C8D5] hover:text-red-400 transition-colors"
            data-testid="gcal-disconnect-btn"
          >
            <Link2Off className="w-3.5 h-3.5" /> Disconnect
          </button>
        </div>
      </div>

      {/* main layout: calendar + day panel */}
      <div className="flex gap-4 flex-col lg:flex-row">

        {/* ── Calendar ── */}
        <Card className="flex-1 min-w-0 p-4 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <button type="button" onClick={() => setCalMonth(m => addMonth(m, -1))}
              className="p-1.5 rounded-lg hover:bg-white/8 text-[#B9C8D5] hover:text-[#F6F1E4] transition-colors"
              data-testid="cal-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#F6F1E4]">{monthLabel(calMonth)}</span>
              <button type="button" onClick={handleRefresh} disabled={loadingMonth}
                className="p-1 rounded-md hover:bg-white/8 text-[#B9C8D5] hover:text-[#F6F1E4] transition-colors disabled:opacity-40"
                data-testid="cal-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${loadingMonth ? "animate-spin" : ""}`} />
              </button>
            </div>
            <button type="button" onClick={() => setCalMonth(m => addMonth(m, 1))}
              className="p-1.5 rounded-lg hover:bg-white/8 text-[#B9C8D5] hover:text-[#F6F1E4] transition-colors"
              data-testid="cal-next-month">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {DOW.map(d => (
              <div key={d} className="text-center text-[10px] font-medium text-[#B9C8D5] py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-px bg-white/5 rounded-lg overflow-hidden">
            {grid.map((dateStr, idx) => {
              if (!dateStr) return <div key={idx} className="bg-[#0B1B2B]/60 aspect-square min-h-[48px]" />;
              const info        = dayMap[dateStr];
              const isToday     = dateStr === today;
              const isSelected  = dateStr === selectedDay;
              const hasDelivery = info?.deliveries?.length > 0;
              const hasPickup   = info?.pickups?.length > 0;
              const dayNum      = parseInt(dateStr.slice(8), 10);
              const allPrinted  = hasDelivery && info.deliveries.every(o => printedCodes.has(o.confirmation_code));

              return (
                <button key={dateStr} type="button"
                  onClick={() => setSelectedDay(s => s === dateStr ? null : dateStr)}
                  className={`relative flex flex-col items-center justify-start pt-1.5 pb-1 min-h-[52px] transition-colors
                    ${isSelected ? "bg-[#FFD166]/15 ring-1 ring-inset ring-[#FFD166]/50" : "bg-[#0B1B2B] hover:bg-white/5"}`}
                  data-testid={`cal-day-${dateStr}`}>
                  <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full
                    ${isToday ? "bg-[#FFD166] text-[#0B1B2B]" : isSelected ? "text-[#FFD166]" : "text-[#B9C8D5]"}`}>
                    {dayNum}
                  </span>
                  {(hasDelivery || hasPickup) && (
                    <div className="flex gap-0.5 mt-0.5">
                      {hasDelivery && <span className={`w-1.5 h-1.5 rounded-full transition-opacity ${allPrinted ? "bg-green-400/40" : "bg-green-400"}`} />}
                      {hasPickup   && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                    </div>
                  )}
                  {info && (info.deliveries.length + info.pickups.length) > 0 && (
                    <span className="text-[9px] text-[#B9C8D5] mt-0.5 leading-none">
                      {new Set([...info.deliveries, ...info.pickups].map(o => o.confirmation_code)).size}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex gap-4 mt-3 px-1 flex-wrap">
            <span className="flex items-center gap-1.5 text-[10px] text-[#B9C8D5]">
              <span className="w-2 h-2 rounded-full bg-green-400" /> Delivery
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-[#B9C8D5]">
              <span className="w-2 h-2 rounded-full bg-amber-400" /> Pickup
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-[#B9C8D5]">
              <span className="w-2 h-2 rounded-full bg-green-400/40" /> All printed
            </span>
          </div>
        </Card>

        {/* ── Day panel ── */}
        <div
          ref={panelRef}
          className={`lg:w-80 xl:w-96 transition-all duration-200 ${selectedDay ? "opacity-100" : "opacity-0 pointer-events-none lg:pointer-events-auto lg:opacity-100"}`}
        >
          {selectedDay ? (
            <Card className="p-4 h-full flex flex-col">
              {/* panel header */}
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-[#B9C8D5]">
                    {new Date(selectedDay + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                  </p>
                  <p className="text-sm text-[#F6F1E4] font-medium mt-0.5">
                    {dayOrders.length} order{dayOrders.length !== 1 ? "s" : ""} · {totalDayLabels} label{totalDayLabels !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {selectedDayKeyCount > 0 && (
                    <Btn
                      onClick={handlePrintSelected}
                      disabled={printing}
                      className="!py-1.5 !px-2.5 text-xs !bg-gradient-to-r !from-green-600 !to-green-500 !shadow-none"
                      data-testid="gcal-print-selected-btn"
                      title={`Print ${selectedDayKeyCount} selected label${selectedDayKeyCount !== 1 ? "s" : ""}`}
                    >
                      <Printer className="w-3.5 h-3.5" /> {selectedDayKeyCount}
                    </Btn>
                  )}
                  <Btn
                    onClick={handlePrintDay}
                    disabled={printing || deliveryOrders.length === 0}
                    className="!py-1.5 !px-3 text-xs"
                    data-testid="gcal-print-day-btn"
                  >
                    <Printer className="w-3.5 h-3.5" /> All
                  </Btn>
                  <button type="button" onClick={() => setSelectedDay(null)}
                    className="p-1 text-[#B9C8D5] hover:text-[#F6F1E4] rounded"
                    data-testid="gcal-close-panel">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* deliveries */}
              {dayMap[selectedDay]?.deliveries?.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <button type="button" onClick={toggleSelectAll}
                      className="hover:opacity-80 transition-opacity"
                      data-testid="gcal-select-all-btn"
                      title={allDaySel === "all" ? "Deselect all" : "Select all delivery labels"}>
                      <CheckIcon state={allDaySel} size="w-3.5 h-3.5" />
                    </button>
                    <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
                    <span className="text-[10px] font-semibold text-green-400 uppercase tracking-wide flex-1">
                      Deliveries ({dayMap[selectedDay].deliveries.length})
                    </span>
                    {selectedDayKeyCount > 0 && (
                      <button type="button" onClick={() => setSelectedLabelKeys(new Set())}
                        className="text-[9px] text-[#B9C8D5] hover:text-[#F6F1E4] underline"
                        data-testid="gcal-clear-selection-btn">
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {dayMap[selectedDay].deliveries.map(order => (
                      <OrderCard
                        key={`d-${order.confirmation_code}`}
                        order={order}
                        onPrintOrder={handlePrintOrder}
                        printing={printing}
                        selectedLabelKeys={selectedLabelKeys}
                        onToggleOrder={toggleOrderKeys}
                        onToggleItem={toggleItemKeys}
                        onToggleKey={toggleKey}
                        printed={printedCodes.has(order.confirmation_code)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* pickups */}
              {dayMap[selectedDay]?.pickups?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                    <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wide">
                      Pickups ({dayMap[selectedDay].pickups.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {dayMap[selectedDay].pickups
                      .filter(o => !dayMap[selectedDay].deliveries.some(d => d.confirmation_code === o.confirmation_code))
                      .map(order => (
                        <OrderCard
                          key={`p-${order.confirmation_code}`}
                          order={order}
                          onPrintOrder={handlePrintOrder}
                          printing={printing}
                          selectedLabelKeys={new Set()}
                          showPrint={false}
                        />
                      ))}
                  </div>
                </div>
              )}

              {dayOrders.length === 0 && (
                <p className="text-sm text-[#B9C8D5] text-center py-8" data-testid="gcal-day-empty">
                  No BabyQuip orders on this day.
                </p>
              )}
            </Card>
          ) : (
            <Card className="p-6 h-full hidden lg:flex flex-col items-center justify-center text-center gap-2">
              <svg className="w-10 h-10 text-[#B9C8D5]/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-[#B9C8D5]">Click a day to see orders</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThirdPartyLabels;
